import { animate, AnimationBuilder, AnimationFactory, AnimationPlayer, style } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NgModule,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SharedModule } from '@shared/shared.module';
import { ImaginarySrcModule, WINDOW } from '@wizbii/angular-utilities';
import { animationFrameScheduler, combineLatest, interval, Observable, of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

type MarqueeDirection = 'left' | 'right';
export interface Logo {
  src: string;
  width: number;
  height?: number;
  alt: string;
}
@UntilDestroy()
@Component({
  selector: 'app-marquee-logo',
  templateUrl: './marquee-logo.component.html',
  styleUrls: ['./marquee-logo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarqueeLogoComponent implements AfterViewInit, OnDestroy {
  @Input() logos!: Logo[];
  @Input() pixelOffset = 10;
  @Input() intervalDuration = 100;
  @Input() marqueeDirection: MarqueeDirection = 'left';

  @ViewChild('group1') group1!: ElementRef;
  @ViewChild('group2') group2!: ElementRef;

  private readonly _containerGroupWidth = 3600;
  private _group1XPosition = this._containerGroupWidth / 2;
  private _group2XPosition = -(this._containerGroupWidth / 2);
  private _width = 100;
  private _player!: AnimationPlayer;
  private _isSmoothAnimationGroup1 = false;
  private _isSmoothAnimationGroup2 = false;

  subscription!: Subscription;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly builder: AnimationBuilder,
    @Inject(WINDOW) readonly window: Window
  ) {}

  ngAfterViewInit(): void {
    this._width = this.window.innerWidth;
    this.subscription = interval(this.intervalDuration, animationFrameScheduler)
      .pipe(
        untilDestroyed(this),
        switchMap(() => {
          return this.getPositionsForStartMoving();
        }),
        map(([group1Position, group2Position], tick) => {
          this.playAnimations(group1Position, group2Position, tick);
        })
      )
      .subscribe();

    this.cdr.detectChanges();
  }

  getPositionsForStartMoving(): Observable<[number, number]> {
    if (this.marqueeDirection === 'left') {
      return this.getPositionsForMoveToLeft();
    }
    return this.getPositionsForMoveToRight();
  }

  getPositionsForMoveToLeft(): Observable<[number, number]> {
    this._group1XPosition -= this.pixelOffset;
    this._group2XPosition -= this.pixelOffset;

    if (this.group1.nativeElement.getBoundingClientRect().left < -this._containerGroupWidth) {
      this._group1XPosition = this._containerGroupWidth;
      this._isSmoothAnimationGroup1 = false;
    }

    if (this.group2.nativeElement.getBoundingClientRect().left < -this._containerGroupWidth) {
      this._group2XPosition = this._containerGroupWidth;
      this._isSmoothAnimationGroup2 = false;
    }

    return combineLatest([of(this._group1XPosition), of(this._group2XPosition)]);
  }

  getPositionsForMoveToRight(): Observable<[number, number]> {
    this._group1XPosition += this.pixelOffset;
    this._group2XPosition += this.pixelOffset;

    if (this.group1.nativeElement.getBoundingClientRect().left > this._width) {
      const posGroup2 = this.group2.nativeElement.getBoundingClientRect().left as number;
      this._group1XPosition = -this._containerGroupWidth + posGroup2;
      this._isSmoothAnimationGroup1 = false;
    }

    if (this.group2.nativeElement.getBoundingClientRect().left > this._width) {
      const posGroup1 = this.group1.nativeElement.getBoundingClientRect().left as number;
      this._group2XPosition = -this._containerGroupWidth + posGroup1;
      this._isSmoothAnimationGroup2 = false;
    }

    return combineLatest([of(this._group1XPosition), of(this._group2XPosition)]);
  }

  private playAnimations(group1Offset: number, group2Offset: number, tick: number) {
    const timingAnimationGroup1 = this.calculateTimingAnimation(tick, this._isSmoothAnimationGroup1);
    const timingAnimationGroup2 = this.calculateTimingAnimation(tick, this._isSmoothAnimationGroup2);

    const animationGroup1: AnimationFactory = this.buildAnimation(
      `translate3d(${group1Offset}px,0, 0)`,
      timingAnimationGroup1
    );
    this._player = animationGroup1.create(this.group1.nativeElement);
    this._player.play();

    const animationGroup2: AnimationFactory = this.buildAnimation(
      `translate3d(${group2Offset}px,0, 0)`,
      timingAnimationGroup2
    );
    this._player = animationGroup2.create(this.group2.nativeElement);
    this._player.play();

    this._isSmoothAnimationGroup1 = true;
    this._isSmoothAnimationGroup2 = true;
  }

  private buildAnimation(translate: string, timing?: number): AnimationFactory {
    return this.builder.build([
      animate(
        timing ?? 0,
        style({
          transform: translate,
        })
      ),
    ]);
  }

  private calculateTimingAnimation(tick: number, isSmooth: boolean): number {
    return tick <= 1 || !isSmooth ? 0 : this.intervalDuration * 10;
  }

  trackByLogoIndex(index: number, _: Logo): number {
    return index;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}

@NgModule({
  imports: [SharedModule, ImaginarySrcModule],
  declarations: [MarqueeLogoComponent],
  exports: [MarqueeLogoComponent],
})
export class MarqueeLogoModule {}
